import { Box, useTheme } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  useAppDispatch,
  useAppSelector,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '@verantos-dev/core-annotation-tool';
import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { getAllAnnotatorsOption } from '../../constants';
import { useBatchProgressDatagridFetch } from '../../hook/useBatchProgressDataGridFetch';
import { setBatchProgressTablePaginationModel } from '../../state/metricsSlice';
import {
  selectBatchProgressTablePage,
  selectBatchProgressTablePageSize,
  selectSelectedAnnotatorId,
} from '../../state/selector';
import { BatchwiseAnnotationProgress } from '../../types/responseTypes';

export const BatchProgressTable = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const batchProgressTablePage = useAppSelector(selectBatchProgressTablePage);
  const batchProgressTablePageSize = useAppSelector(selectBatchProgressTablePageSize);
  const selectedAnnotatorId = useAppSelector(selectSelectedAnnotatorId);
  const allAnnotatorOption = getAllAnnotatorsOption(formatMessage);
  const isAllAnnotatorsSelected = selectedAnnotatorId === allAnnotatorOption.id;

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setBatchProgressTablePaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const { isFetching } = useBatchProgressDatagridFetch() || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(
        setBatchProgressTablePaginationModel({ page: 0, pageSize: batchProgressTablePageSize })
      );
    }
  }, [isFetching]);

  const columns: GridColDef<BatchwiseAnnotationProgress>[] = useMemo(
    () => [
      {
        field: 'srNo',
        headerName: 'Sr No.',
        minWidth: 80,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      {
        field: 'batchName',
        headerName: 'Batch name',
        minWidth: 220,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'totalEncounters',
        headerName: 'Total encounters',
        minWidth: 220,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          if (isAllAnnotatorsSelected) {
            return params.row.totalEncounterCount ?? 0;
          }
          // For a specific annotator, total encounters = their pending + all-time completed tasks
          return (params.row.pendingTaskCount ?? 0) + (params.row.totalCompletedTaskCount ?? 0);
        },
      },
      {
        field: 'completedTaskCount',
        headerName: 'Encounters completed',
        minWidth: 220,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          if (isAllAnnotatorsSelected) {
            // Each encounter has 2 annotator tasks; divide by 2 for encounter-level count
            return Math.floor((params.row.completedTaskCount ?? 0) / 2);
          }
          return params.row.completedTaskCount ?? 0;
        },
      },
      {
        field: 'percentageCompleted',
        headerName: 'Percentage completed',
        minWidth: 220,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const completed = params.row.completedTaskCount ?? 0;

          if (isAllAnnotatorsSelected) {
            const total = params.row.totalEncounterCount ?? 0;
            if (total === 0) return '0.00%';

            // Each encounter has 2 annotator tasks; divide by 2 for encounter-level percentage
            const completedEncounters = Math.floor(completed / 2);
            const percentage = (completedEncounters * 100) / total;

            return `${percentage.toFixed(2)}%`;
          }

          // For a specific annotator, compute percentage from their own task counts
          const pending = params.row.pendingTaskCount ?? 0;
          const totalCompletedAllTime = params.row.totalCompletedTaskCount ?? 0;
          const total = pending + totalCompletedAllTime;
          if (total === 0) return '0.00%';

          const percentage = (completed * 100) / total;
          return `${percentage.toFixed(2)}%`;
        },
      },
    ],
    [formatMessage, theme, isAllAnnotatorsSelected]
  );

  return (
    <Box>
      <DataGridLayout id="batchwise-progress-data-grid">
        <DataGrid
          id="batchwise-progress-data-grid"
          mode="client"
          pagination
          loadOnFetch
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          paginationModel={{ page: batchProgressTablePage, pageSize: batchProgressTablePageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSearchParamSync
          preventUrlReadWhenSyncDisabled
          getRowId={(row) => row.batchName}
          columns={columns}
          useFetchDataGridData={useBatchProgressDatagridFetch}
          initialState={{
            pinnedColumns: {
              left: ['srNo', 'batchName'],
            },
          }}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
